import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormHelperText, Typography } from "@mui/material";
import Button from "@components/Button/Button";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import FormDivider from "@components/FormDivider/FormDivider";
import FormItem from "@components/FormItem/FormItem";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import StepperControls from "@components/StepperControls/StepperControls.tsx";
import { useNotification } from "@context/NotificationContext.tsx";
import { useDocumentUpload } from "@features/vessel-call/create/hooks/useDocumentUpload";
import { useResubmitDepartureRequest } from "@features/vessel-call/vessel-call-status/hooks/useResubmitDepartureRequest";
import { usePersistentForm } from "@hooks/usePersistentForm";
import { getPastMovementServices } from "@services/movementServicesApi";
import { SasTokenPrefix } from "@services/user/types";
import type { Task } from "@services/vessel-call/types";
import { useQuery } from "@tanstack/react-query";
import { paths } from "@utils/navigation";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useCreateDepartureRequest } from "../../../hooks/useCreateDepartureRequest";
import {
  DEPARTURE_BLOCKER,
  type DepartureAvailability,
} from "../../availability/departureAvailability";
import { FORM_FIELDS } from "../../constants";
import { useClearanceTypeQuery } from "../../hooks/useClearanceTypeQuery";
import {
  DEPARTURE_REMARKS_MAX_CHARS,
  getRequestDepartureFormSchema,
  type RequestDepartureFormData,
} from "../../schema/requestDepartureFormSchema";
import {
  PERSIST_KEY,
  REQUEST_DEPARTURE_DEFAULT_VALUES,
  useRequestDepartureFormStore,
} from "../../store/useRequestDepartureForm.store";
import CrewListDialog from "../CrewListDialog/CrewListDialog";
import DepartureDocument from "../DepartureDocument/DepartureDocument";
import RequestDepartureDetails from "../RequestDepartureDetails/RequestDepartureDetails";
import { useDepartureUploadAdditionalDocuments } from "./useDepartureUploadAdditionalDocuments";

const service = "bff-vessel-call";
const TEST_ID_BASE = "request-departure-container";
const LANG_BASE = "vessel-call.request-departure";

type DepartureRequestProps = {
  refetch: () => void;
  isResubmitMode?: boolean;
  isCustomsBypass?: boolean;
  task?: Task;
  status?: string;
  vesselId?: string;
  submittedBy?: string;
  submittedByRole?: string;
  initialCustomsClearance?: { name: string; url: string };
  hadCancelledDepartures: boolean;
  departureAvailability?: DepartureAvailability;
  refetchDepartureAvailability?: () => Promise<DepartureAvailability | undefined>;
};

const filterMovements = (items: Array<{ from?: string; to?: string; status?: string }> = []) => {
  const banned = [
    { from: "SEA", to: "FOAA" },
    { from: "FOAA", to: "FOAA" },
    { from: "FOAA", to: "SEA" },
  ];

  return items.filter((item) => {
    const isBannedRoute = banned.some((b) => b.from === item.from && b.to === item.to);

    const isCancelled = item.status?.startsWith("CANCELLED_BY");

    return !isBannedRoute && !isCancelled;
  });
};

export type UpdateFormData = <K extends keyof RequestDepartureFormData>(
  key: K,
  value: RequestDepartureFormData[K] | undefined,
) => void;

const DepartureRequest: React.FC<DepartureRequestProps> = ({
  refetch,
  isResubmitMode = false,
  isCustomsBypass = false,
  task,
  status,
  vesselId,
  submittedBy,
  submittedByRole,
  initialCustomsClearance,
  hadCancelledDepartures,
  departureAvailability,
  refetchDepartureAvailability,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { id: vesselCallId } = useParams();
  const { clearFilesForKey } = useUploadStore();

  const [isCrewListOpen, setIsCrewListOpen] = useState(false);
  const [isConformationWindowOpen, setIsConformationWindowOpen] = useState(false);

  const { data: pastMovementsData, isLoading: isPastMovementsLoading } = useQuery({
    queryKey: ["getPastMovementServices"],
    queryFn: () => {
      return getPastMovementServices({
        service,
        vesselCallId: vesselCallId as string,
      });
    },
  });

  const { data: clearanceData } = useClearanceTypeQuery(vesselCallId);
  const isDraftRequired =
    clearanceData?.displayRequestClearanceCheckbox === false &&
    !clearanceData?.hasVisitedAnchorageOnly;

  const pastMovements = useMemo(() => {
    return pastMovementsData?.items || [];
  }, [pastMovementsData]);

  const storeValues: Partial<RequestDepartureFormData> = useRequestDepartureFormStore.getState();

  const schema = useMemo(
    () => getRequestDepartureFormSchema(t, isDraftRequired, isCustomsBypass),
    [t, isDraftRequired, isCustomsBypass],
  );
  const { setField, setAll, resetForm } = useRequestDepartureFormStore();

  const form = usePersistentForm<RequestDepartureFormData>({
    schema,
    defaultValues: REQUEST_DEPARTURE_DEFAULT_VALUES as RequestDepartureFormData,
    storeValues,
    setStoreValues: setAll,
    resetStore: resetForm,
    persistKey: PERSIST_KEY,
    dateKeys: [],
  });

  const isFirstVisit = form.watch(FORM_FIELDS.FIRST_VISIT);
  const hasCustomsClearance = isResubmitMode && !!initialCustomsClearance?.url;

  useEffect(() => {
    const filteredMovements = filterMovements(pastMovements);
    if (filteredMovements.length > 0 && !isPastMovementsLoading) {
      form.setValue(FORM_FIELDS.FIRST_VISIT, false, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [isFirstVisit, form.setValue, pastMovements, isPastMovementsLoading]);

  const { onSubmit, error, validateBeforeConfirm } = !isResubmitMode
    ? useCreateDepartureRequest({
        vesselCallId: vesselCallId ?? "",
        resetForm,
        refetchDepartureDetails: refetch,
        refetchDepartureAvailability,
      })
    : {
        ...useResubmitDepartureRequest({
          vesselCallId: vesselCallId ?? "",
          task: task!,
          status: status ?? "",
          resetForm,
          refetchDepartureDetails: refetch,
          vesselId: vesselId ?? "",
          submittedBy: submittedBy ?? "",
          submittedByRole: submittedByRole ?? "",
          initialCustomsClearance: initialCustomsClearance,
          refetchDepartureAvailability,
        }),
        error: null,
        validateBeforeConfirm: async () => true,
      };

  const preSailingMasterDeclaration = useDocumentUpload(
    FORM_FIELDS.PRE_SAILING_MASTER_DECLARATION,
    SasTokenPrefix.VESSEL_CALL,
    form,
    useRequestDepartureFormStore,
  );
  const cargoManifest = useDocumentUpload(
    FORM_FIELDS.CARGO_MANIFEST,
    SasTokenPrefix.VESSEL_CALL,
    form,
    useRequestDepartureFormStore,
  );
  const customsClearance = useDocumentUpload(
    FORM_FIELDS.CUSTOMS_CLEARANCE,
    SasTokenPrefix.VESSEL_CALL,
    form,
    useRequestDepartureFormStore,
  );
  const additionalDocuments = useDepartureUploadAdditionalDocuments(
    FORM_FIELDS.ADDITIONAL_DOCUMENTS,
    SasTokenPrefix.VESSEL_CALL,
    form,
    useRequestDepartureFormStore,
  );

  const updateFormData: UpdateFormData = (key, value) => {
    setField(key as any, value as any);
  };

  const handleAdditionalDocumentMetadataChange = (key: string, value: unknown) => {
    updateFormData(key as keyof RequestDepartureFormData, value as never);
  };

  const handleFieldChange = async (certType: any, value: File[]) => {
    const [selectedFile] = value ?? [];

    if (certType === FORM_FIELDS.CARGO_MANIFEST) {
      cargoManifest.handleUpload(value);
      if (selectedFile) {
        cargoManifest.setUploadEntries([{ name: selectedFile.name, type: selectedFile.type }]);
      }
    }
    if (certType === FORM_FIELDS.PRE_SAILING_MASTER_DECLARATION) {
      preSailingMasterDeclaration.handleUpload(value);
      if (selectedFile) {
        preSailingMasterDeclaration.setUploadEntries([
          { name: selectedFile.name, type: selectedFile.type },
        ]);
      }
    }
    if (certType === FORM_FIELDS.CUSTOMS_CLEARANCE) {
      customsClearance.handleUpload(value);
      if (selectedFile) {
        customsClearance.setUploadEntries([{ name: selectedFile.name, type: selectedFile.type }]);
      }
    }
    if (certType === FORM_FIELDS.ADDITIONAL_DOCUMENTS) {
      additionalDocuments.handleUpload(value);
      if (isCustomsBypass && selectedFile) {
        setField(FORM_FIELDS.CUSTOMS_CLEARANCE_UPLOAD_HAPPENED, true);
        form.setValue(FORM_FIELDS.CUSTOMS_CLEARANCE_UPLOAD_HAPPENED, true, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
  };

  const cancelForm = useCallback(() => {
    if (typeof (form as { clear?: () => void }).clear === "function") {
      (form as { clear: () => void }).clear();
    } else {
      resetForm();
    }
    clearFilesForKey(FORM_FIELDS.PRE_SAILING_MASTER_DECLARATION);
    clearFilesForKey(FORM_FIELDS.CARGO_MANIFEST);
    clearFilesForKey(FORM_FIELDS.CUSTOMS_CLEARANCE);
    clearFilesForKey(FORM_FIELDS.ADDITIONAL_DOCUMENTS);
    if (isResubmitMode) {
      refetch();
    } else {
      navigate(`/${paths.vesselCall.index}`, { replace: true });
    }
  }, [form, navigate, clearFilesForKey, resetForm]);

  const onValid = async () => {
    const latestAvailability = refetchDepartureAvailability
      ? await refetchDepartureAvailability()
      : departureAvailability;
    const hasDisputeBlocker = latestAvailability?.blockers.some(
      (blocker) => blocker.code === DEPARTURE_BLOCKER.INVALID_DISPUTE_STATUS,
    );
    if (latestAvailability && !latestAvailability.isAvailable && !hasDisputeBlocker) {
      notify({
        title: t(`${LANG_BASE}.availability.toast-title`),
        description: latestAvailability.blockers
          .map((blocker) =>
            t(`${LANG_BASE}.availability.blockers.${blocker.code}`, {
              count: blocker.count,
              terminals: blocker.terminals?.join(", "),
            }),
          )
          .join("\n"),
        type: "error",
      });
      return;
    }

    setAll(form.getValues());
    const canOpenConfirmationWindow = await validateBeforeConfirm();
    if (!canOpenConfirmationWindow) {
      return;
    }
    setIsConformationWindowOpen(true);
  };

  const onInvalid = () => {
    notify({
      title: t(`${LANG_BASE}.notifications.request-submitted-error-title`),
      description: t(`${LANG_BASE}.notifications.request-submitted-error-description`),
      type: "error",
    });
  };

  const handleSubmit = () => {
    form.handleSubmit(onValid, onInvalid)();
  };

  const confirmSubmit = () => {
    setIsConformationWindowOpen(false);
    onSubmit();
  };

  return (
    <div className="flex flex-col gap-4 mb-8" data-testid={TEST_ID_BASE}>
      <Typography variant="h6">{t(`vessel-call.request-departure.title`)}</Typography>
      <input type="hidden" {...form.register(FORM_FIELDS.FIRST_VISIT)} />
      <RequestDepartureDetails
        formData={form}
        updateFormData={updateFormData}
        hadCancelledDepartures={hadCancelledDepartures}
      />
      <DepartureDocument
        testId={TEST_ID_BASE}
        langBase={LANG_BASE}
        formData={form}
        documentType={FORM_FIELDS.PRE_SAILING_MASTER_DECLARATION}
        files={preSailingMasterDeclaration.uploadEntries}
        setFiles={() => preSailingMasterDeclaration.setUploadEntries([])}
        handleFieldChange={handleFieldChange}
        removeFn={() => preSailingMasterDeclaration.handleRemove()}
        notSuccess={preSailingMasterDeclaration.notSuccess}
        isLoading={preSailingMasterDeclaration.isPending}
      />
      {!isFirstVisit && (
        <DepartureDocument
          testId={TEST_ID_BASE}
          langBase={LANG_BASE}
          formData={form}
          documentType={FORM_FIELDS.CARGO_MANIFEST}
          files={cargoManifest.uploadEntries}
          setFiles={() => cargoManifest.setUploadEntries([])}
          handleFieldChange={handleFieldChange}
          removeFn={() => cargoManifest.handleRemove()}
          notSuccess={cargoManifest.notSuccess}
          isLoading={cargoManifest.isPending}
        />
      )}
      {hasCustomsClearance && (
        <DepartureDocument
          testId={TEST_ID_BASE}
          langBase={LANG_BASE}
          formData={form}
          documentType={FORM_FIELDS.CUSTOMS_CLEARANCE}
          files={customsClearance.uploadEntries}
          setFiles={() => customsClearance.setUploadEntries([])}
          handleFieldChange={handleFieldChange}
          removeFn={() => customsClearance.handleRemove()}
          notSuccess={customsClearance.notSuccess}
          isLoading={customsClearance.isPending}
        />
      )}
      <FormProvider {...form}>
        <FormItem<RequestDepartureFormData, typeof FORM_FIELDS.REMARKS>
          name={FORM_FIELDS.REMARKS}
          type="textarea"
          label={t(`${LANG_BASE}.form-fields.remarks`)}
          testId={`${TEST_ID_BASE}-remarks`}
          onFieldChange={updateFormData}
          maxChars={DEPARTURE_REMARKS_MAX_CHARS}
          minRows={3}
          showCharCounter
        />
      </FormProvider>
      <DepartureDocument
        testId={TEST_ID_BASE}
        langBase={LANG_BASE}
        formData={form}
        documentType={FORM_FIELDS.ADDITIONAL_DOCUMENTS}
        files={additionalDocuments.uploadEntries}
        setFiles={additionalDocuments.setUploadEntries}
        handleFieldChange={handleFieldChange}
        removeFn={additionalDocuments.handleRemove}
        onFieldChange={handleAdditionalDocumentMetadataChange}
        notSuccess={additionalDocuments.notSuccess}
        maxFiles={25}
        isLoading={additionalDocuments.isPending}
        isFileLoading={(_, index) => additionalDocuments.uploadingIndexes.includes(index)}
      />
      {!isFirstVisit && (
        <div className="my-4">
          <Button
            onClick={() => setIsCrewListOpen(true)}
            variant="outlined"
            label={<span className="normal-case font-medium">{t(`${LANG_BASE}.show-button`)}</span>}
            testId={`${TEST_ID_BASE}-show-button`}
          />
        </div>
      )}
      <FormDivider />
      <div className="mt-4">
        <FormHelperText
          component="div"
          sx={{ color: "error.main" }}
          data-testid={`${TEST_ID_BASE}-error`}
        >
          {error}
        </FormHelperText>
        <StepperControls
          cancel={cancelForm}
          complete={handleSubmit}
          completeLabelTranslationKey={"stepper-controls.submit-request"}
        />
      </div>
      <CrewListDialog
        testId={`${TEST_ID_BASE}-crew-list-dialog`}
        langBase={`${LANG_BASE}.crew-list`}
        isOpen={isCrewListOpen}
        setIsCrewListIsOpen={setIsCrewListOpen}
      />
      <GenericDialog
        open={isConformationWindowOpen}
        title={t(`${LANG_BASE}.confirmationTitle`)}
        content={t(`${LANG_BASE}.confirmationMessage`)}
        confirmButtonText={t("common.confirm")}
        cancelButtonText={t("common.cancel")}
        handleCloseDialog={() => setIsConformationWindowOpen(false)}
        confirmAction={confirmSubmit}
      />
    </div>
  );
};

export default DepartureRequest;
